import React, { memo, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import router from 'next/router';
import Link from 'next/link';

import { useTranslation } from 'next-i18next';
import { Icon, IconName, Spinner, Tooltip } from 'components';
import { Avatar } from 'components/Avatar/Avatar.component';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import {
  LeftSidebarBottomSkeleton,
  LeftSidebarTopSkeleton,
} from 'components/skeletons/dashboard.component';
import { GetHuzzleVerifiedModal } from 'components/GetHuzzleVerifiedModal/GetHuzzleVerifiedModal.component';
import ProgressBar from 'components/ComponentV2/ProgressBar';
import { useResponsive } from 'hooks/useResponsive';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { CHROME_EXTENSION_STORE_LINK } from 'utils/urls';
import { AutofillTooltipContent } from '../AutofillTooltip/AutofillTooltip.component';

import styles from './SidebarLeft.module.scss';

export interface SidebarLeftProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  onOpenMatchingPreferencesModal?: () => void;
  cvUpload: () => void;
}
export interface EducationHistory {
  attributes: {
    end_date: string;
    subject_name: string;
    university: {
      data: {
        attributes: {
          name: string;
        };
      };
    };
    school_name: string;
  };
}

export const SidebarLeft: React.FC<SidebarLeftProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    cvUpload,
  }: SidebarLeftProps) => {
    const contentClassNames = cn(
      {
        [styles.SidebarLeft]: true,
      },
      className
    );
    const [t] = useTranslation('candidate-dashboard');

    const screens = useResponsive();
    const [latestEducation, setLatestEducation] = useState('');
    const [latestSubject, setLatestSubject] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const { candidate } = useAuth();
    const { candidateDashboard } = useCandidateDashboard();
    const { opportunityMatches } = useCandidateMatches();
    const onboarding_completed = candidate?.onboarding_completed;

    const {
      saved_application_count,
      extension_installed,
      started_application_count,
      registered_touchpoint_count,
      applied_touchpoint_count,
      match_viewed,
      profile_complete,
      reviewed_preferences,
      applied_for_job_internship,
    } = candidateDashboard || {};
    const calculateIsVerified = () => {
      const calculatedIsVerified = sectionData.every(
        (section) => section.isCompleted
      );
      setIsVerified(calculatedIsVerified);
    };
    const candidateFullName = useMemo(() => {
      if (candidate) {
        const fullName = `${candidate.first_name || ''} ${
          candidate.last_name || ''
        }`;
        return fullName.trim() || 'Add your name';
      }
    }, [candidate]);
    useEffect(() => {
      calculateIsVerified();
    }, []);
    useEffect(() => {
      if (screens.sm) {
        setShowAllSections(false);
      } else {
        setShowAllSections(true);
      }
    }, [screens]);
    useEffect(() => {
      const findLatestEducation = () => {
        if (candidate?.education_histories) {
          const sortedEducations = candidate?.education_histories.data
            ?.filter(
              (education: EducationHistory) => education.attributes.end_date
            )
            .sort((a: EducationHistory, b: EducationHistory) => {
              const dateA = new Date(a.attributes.end_date);
              const dateB = new Date(b.attributes.end_date);
              return dateB.getTime() - dateA.getTime();
            });

          setLatestEducation(
            sortedEducations[0]?.attributes?.university?.data?.attributes
              ?.name || sortedEducations[0]?.attributes?.school_name
          );
          setLatestSubject(sortedEducations[0]?.attributes.subject_name || '');
        }
      };

      findLatestEducation();
    }, [candidate]);

    const opportunities = [
      {
        iconName: 'icon_bookmark',
        text: 'Saved',
        count: saved_application_count,
        badgeCount: 1,
      },
      {
        iconName: 'icon_clock',
        text: 'Started',
        count: started_application_count,
      },
      {
        iconName: 'icon_briefcase',
        text: 'Applied',
        count: applied_touchpoint_count,
      },
      {
        iconName: 'icon_party',
        text: 'Registered',
        count: registered_touchpoint_count,
      },
    ];
    // Huzzle Verified tasks list
    const sectionData = [
      {
        iconName: 'icon_sport-mode',
        text: 'Complete onboarding',
        redirectionAction: () => {
          window.open(PAGE_ROUTES.CANDIDATE_ONBOARDING, '_blank');
        },
        isCompleted: onboarding_completed,
        ctaText: 'Complete onboarding',
      },
      {
        iconName: 'icon_inbox',
        text: 'Check your first match',
        isCompleted: match_viewed,
        redirectionAction: () => {
          if (opportunityMatches.length > 0) {
            const match = opportunityMatches[0];
            const url = `${PAGE_ROUTES.CANDIDATE_MATCHES}/${match.matchable_slug}`;
            router.push(url);
          }
        },
        ctaText: 'Check Matches',
      },
      {
        iconName: 'icon_profile',
        text: 'Complete your profile',
        isCompleted: profile_complete,
        pageURl: PAGE_ROUTES.CANDIDATE_PROFILE,
        ctaText: 'Complete Profile',
      },
      {
        iconName: 'icon_magic-wand',
        text: 'Get Autofill extension',
        isCompleted: extension_installed,
        redirectionAction: () => {
          window.open(CHROME_EXTENSION_STORE_LINK, '_blank');
        },
        ctaText: 'Get Autofill Extension',
      },
      {
        iconName: 'icon_briefcase',
        text: 'Apply to 1 job or internship',
        isCompleted: applied_for_job_internship,
        pageURl: PAGE_ROUTES.SEARCH_RESULTS,
        ctaText: 'Explore jobs & internships',
      },
      {
        iconName: 'icon_filter-2',
        text: 'Adjust your preferences',
        isCompleted: reviewed_preferences,
        redirectionAction: () =>
          router.push(
            `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
          ),
        ctaText: 'Matching preferences',
      },
    ];
    useEffect(() => {
      calculateIsVerified();
    }, [sectionData]);
    const completedSections = sectionData.filter(
      (section) => section.isCompleted
    );
    const [showAllSections, setShowAllSections] = useState(false);
    const percentCompleted = Math.min(
        100,
        Math.round((completedSections.length / 6) * 100)
      ),
      completedTopics = completedSections.length;

    const HuzzleVerifiedTooltip = useMemo(() => {
      return (
        <div className={styles.tooltipContent}>
          <div className={styles.wrapperContent}>
            <Icon iconName="icon_medal" className={styles.icon} size="auto" />
            <div className={styles.tooltipContainer}>
              <div className={styles.tooltipHeading}>
                {t('sidebar-left.get-huzzle-verified')}
              </div>
              <div className={styles.tooltipText}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('sidebar-left.huzzle-verified-modal-text'),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }, []);

    const unVerifiedSidebar = useMemo(() => {
      return (
        <>
          <div
            className={cn(contentClassNames, {
              [styles.addBackground]: showAllSections,
            })}
            data-testid="SidebarLeft"
          >
            <div className={styles.wrapper}>
              {candidateFullName ? (
                <>
                  <div className={styles.profileWrapper}>
                    <Row
                      align="center"
                      columnGap={10}
                      className={styles.header}
                    >
                      <Avatar
                        name={candidateFullName || ''}
                        icon={candidate?.avatar_url}
                        size="small"
                        className={styles.avatar}
                      />
                      <div>
                        <p
                          className={styles.name}
                          onClick={() =>
                            router.push(PAGE_ROUTES.CANDIDATE_PROFILE)
                          }
                        >
                          {candidateFullName}
                        </p>
                      </div>
                    </Row>
                  </div>

                  {!isVerified && (
                    <Tooltip
                      content={HuzzleVerifiedTooltip}
                      containerClassName={styles.tooltipContent}
                      childrenClassName={styles.childrenTooltip}
                      className={styles.tooltip}
                      position="right"
                      yAlign="top"
                    >
                      <div className={styles.sectionWrapper}>
                        <div
                          className={styles.section}
                          onClick={() => screens.sm && GetHuzzleVerifiedModal()}
                        >
                          <Row className={styles.sectionCont}>
                            <div className={styles.sectionTitleContainer}>
                              <Icon
                                iconName="icon_medal"
                                size="xmediumplus"
                                className={styles.medalIcon}
                              />
                              <p className={styles.sectionTitleText}>
                                {t('sidebar-left.get-huzzle-verified')}
                              </p>
                            </div>
                            {completedTopics < 3 ? (
                              <div className={styles.completionStatus}>
                                <Icon iconName="icon_progress" size="xsmall" />
                                <p>{t('sidebar-left.not-verified')}</p>
                              </div>
                            ) : (
                              <div className={styles.completionStatus}>
                                <Icon iconName="icon_progress" size="xsmall" />

                                <p>{t('sidebar-left.almost-done')}</p>
                              </div>
                            )}
                          </Row>

                          <Row className={styles.progressBarContianer}>
                            <ProgressBar
                              className={styles.progressBar}
                              color={'Primary'}
                              percentCompleted={percentCompleted}
                            />
                            <div className={styles.progessContainer}>
                              <div className={styles.progressStats}>
                                <p className={styles.progressPercent}>
                                  {percentCompleted}%
                                </p>
                                <p className={styles.progressText}>
                                  {t('sidebar-left.completed-topics', {
                                    completedTopics: completedTopics,
                                  })}
                                </p>
                              </div>
                              {!showAllSections && (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllSections(!showAllSections);
                                  }}
                                >
                                  <Icon
                                    iconName="icon_arrow-down"
                                    size="small"
                                    className={styles.downArrow}
                                  />
                                </div>
                              )}
                            </div>
                          </Row>
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </>
              ) : (
                <LeftSidebarTopSkeleton />
              )}
            </div>

            <div
              className={cn(styles.sectionContainers, {
                [styles.hideContainers]: !showAllSections,
              })}
            >
              {candidateDashboard ? (
                showAllSections &&
                sectionData.map((section, index) => (
                  <section
                    key={index}
                    className={cn(styles.sectionContainer, {
                      [styles.blueBackground]: section.isCompleted,
                      [styles.hidesectionContainer]: !showAllSections,
                    })}
                  >
                    <Row className={styles.sectionRow}>
                      {section.text === 'Get Autofill extension' ? (
                        <Tooltip
                          content={<AutofillTooltipContent />}
                          containerClassName={styles.autofillTooltip}
                          className={styles.menu}
                          position="left"
                          yAlign="top"
                        >
                          <div className={styles.sectionRowContainerWrapper}>
                            <section className={styles.sectionRowContainer}>
                              <Icon
                                iconName={section.iconName as IconName}
                                className={cn(styles.icon, {
                                  [styles.blueBackgroundIcon1]:
                                    section.isCompleted,
                                })}
                              />
                              <p
                                className={cn(styles.textss, {
                                  [styles.backgroundText]: section.isCompleted,
                                })}
                              >
                                {section.text}
                              </p>
                            </section>
                            <Icon
                              iconName="icon_check"
                              size="small"
                              className={cn(styles.iconCheck, {
                                [styles.blueBackgroundIcon]:
                                  section.isCompleted,
                              })}
                            />
                          </div>
                        </Tooltip>
                      ) : (
                        <>
                          <section className={styles.sectionRowContainer}>
                            <Icon
                              iconName={section.iconName as IconName}
                              className={cn(styles.icon, {
                                [styles.blueBackgroundIcon1]:
                                  section.isCompleted,
                              })}
                            />
                            <p
                              className={cn(styles.textss, {
                                [styles.backgroundText]: section.isCompleted,
                              })}
                            >
                              {section.text}
                            </p>
                          </section>
                          <Icon
                            iconName="icon_check"
                            size="small"
                            className={cn(styles.iconCheck, {
                              [styles.blueBackgroundIcon]: section.isCompleted,
                            })}
                          />
                        </>
                      )}
                    </Row>
                  </section>
                ))
              ) : (
                <div className={styles.spinnerSectionData}>
                  <LeftSidebarBottomSkeleton />
                </div>
              )}

              {candidateDashboard
                ? sectionData.map((section, index) => (
                    <section
                      key={index}
                      className={cn(styles.sectionContainerDesktop, {
                        [styles.blueBackground]: section.isCompleted,
                      })}
                    >
                      <Row
                        className={styles.sectionRow}
                        onClick={() =>
                          section.pageURl
                            ? router.push(section.pageURl)
                            : section.redirectionAction &&
                              section.redirectionAction()
                        }
                      >
                        {section.text === 'Get Autofill extension' ? (
                          <Tooltip
                            content={<AutofillTooltipContent />}
                            containerClassName={styles.autofillTooltip}
                            className={styles.menu}
                            position="right"
                            yAlign="top"
                          >
                            <div className={styles.sectionRowContainerWrapper}>
                              <section className={styles.sectionRowContainer}>
                                <Icon
                                  iconName={section.iconName as IconName}
                                  className={cn(styles.icon, {
                                    [styles.blueBackgroundIcon1]:
                                      section.isCompleted,
                                  })}
                                />
                                <p
                                  className={cn(styles.textss, {
                                    [styles.backgroundText]:
                                      section.isCompleted,
                                  })}
                                >
                                  {section.text}
                                </p>
                              </section>
                              <Icon
                                iconName="icon_check"
                                size="small"
                                className={cn(styles.iconCheck, {
                                  [styles.blueBackgroundIcon]:
                                    section.isCompleted,
                                })}
                              />
                            </div>
                          </Tooltip>
                        ) : (
                          <>
                            <section className={styles.sectionRowContainer}>
                              <Icon
                                iconName={section.iconName as IconName}
                                className={cn(styles.icon, {
                                  [styles.blueBackgroundIcon1]:
                                    section.isCompleted,
                                })}
                              />
                              <p
                                className={cn(styles.textss, {
                                  [styles.backgroundText]: section.isCompleted,
                                })}
                              >
                                {section.text}
                              </p>
                            </section>
                            <Icon
                              iconName="icon_check"
                              size="small"
                              className={cn(styles.iconCheck, {
                                [styles.blueBackgroundIcon]:
                                  section.isCompleted,
                              })}
                            />
                          </>
                        )}
                      </Row>
                    </section>
                  ))
                : null}
            </div>
            <>
              <div className={styles.divider}></div>
              <div
                className={cn(styles.custom, {
                  [styles.hideCustom]: !showAllSections,
                })}
              >
                <ButtonV3
                  className={styles.routeButton}
                  color={screens.sm ? 'primary' : 'secondary'}
                  isFullWidth
                  onClick={() => {
                    const incompleteSection = sectionData.find(
                      (section) => !section.isCompleted
                    );
                    if (incompleteSection) {
                      if (incompleteSection.pageURl) {
                        router.push(incompleteSection.pageURl);
                      } else if (incompleteSection.redirectionAction) {
                        incompleteSection.redirectionAction();
                      }
                    }
                  }}
                >
                  {sectionData.find((section) => !section.isCompleted)
                    ?.ctaText || 'Complete Profile'}
                </ButtonV3>
                {showAllSections && (
                  <div
                    className={styles.showLess}
                    onClick={() => setShowAllSections(false)}
                  >
                    {t('sidebar-left.show-less')}
                  </div>
                )}
              </div>
            </>
          </div>
        </>
      );
    }, [isVerified, candidateDashboard, showAllSections, setShowAllSections]);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="SidebarLeft"
      >
        {isVerified ? (
          <>
            <div className={styles.verifiedContainer}>
              <Row
                align="center"
                className={cn(styles.verifiedHeader, styles.hidePhone)}
              >
                {candidateFullName ? (
                  <>
                    <div className={styles.avatarWrapper}>
                      <Avatar
                        name={candidateFullName || ' '}
                        icon={candidate?.avatar_url}
                        size="xxlarge"
                        className={styles.avatar}
                      />
                      {candidate?.display_huzzle_verified && (
                        <div className={styles.iconStarWrapper}>
                          <Icon
                            iconName="icon_star"
                            className={styles.iconStar}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className={styles.verifiedName}>
                        {candidateFullName ? candidateFullName : ''}
                      </p>
                      <div className={styles.verifiedText}>
                        <p>{latestSubject}</p>
                        <p>{latestEducation}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <Spinner />
                )}
              </Row>
              <div className={styles.dividerDiv}></div>

              <Row className={styles.verifiedHeader1}>
                <section
                  onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
                >
                  Manage Profile
                </section>
                <section
                  onClick={() =>
                    router.push(
                      `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
                    )
                  }
                >
                  Change matching preferences
                </section>
                <section onClick={() => cvUpload()}>Download CV</section>
              </Row>
            </div>
            <div className={styles.verifiedHeader2}>
              <Row className={styles.opportunity}>My opportunities</Row>

              {candidate ? (
                opportunities.map((opportunity, index) => (
                  <Link
                    key={index}
                    href={{
                      pathname: PAGE_ROUTES.MANAGER,
                      query: { status: opportunity.text },
                    }}
                    passHref
                  >
                    <a className={styles.opportunityRow}>
                      <div className={styles.optionRowContainer}>
                        <Icon iconName={opportunity.iconName as IconName} />
                        <p className={styles.opportunityText}>
                          {opportunity.text}
                        </p>
                      </div>

                      {opportunity.count !== undefined && (
                        <span className={styles.count}>
                          {opportunity.count}
                        </span>
                      )}
                    </a>
                  </Link>
                ))
              ) : (
                <div className={styles.spinner}>
                  <Spinner />
                </div>
              )}
              <Row className={styles.openManager} columnGap={12}>
                <Link href={PAGE_ROUTES.MANAGER} passHref>
                  <a className={styles.openMangerText}>Open manager</a>
                </Link>
                <Link href={PAGE_ROUTES.SEARCH_RESULTS} passHref>
                  <a className={styles.openMangerText}>Explore more</a>
                </Link>
              </Row>
            </div>
            <div className={styles.showMoblileVerified}>
              {!isVerified && unVerifiedSidebar}
            </div>
          </>
        ) : (
          <div>{unVerifiedSidebar}</div>
        )}
      </div>
    );
  }
);

SidebarLeft.displayName = 'SidebarLeft';
